import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	ListItemText,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { FormikHelpers, useFormik } from "formik";
import React, {
	Dispatch,
	SetStateAction,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import debounce from "lodash/debounce";
import ConfirmOtp from "../confirm-otp/confirm-otp.component";
import {
	checkIfStringHas15Characters,
	checkIfStringHasAtLeastOneNumber,
	checkIfStringHasLowerCase,
	checkIfStringHasUpperCase,
} from "@screens/register-new/functions";
import { Link } from "react-router-dom";
import Bullet from "../bullet/bullet.component";
import { Phone } from "@global/phone";
import CountryPickerDb from "../country-picker-db/country-picker-db.component";
import useBreakpoint from "@utils/breakPoint";
import { Box } from "@mui/system";
import { RegisterNewSetp1LoginInformationValues } from "@screens/register-new/step-1/setp1.component";
export interface FormOptions<T> {
	formName: string;
	setFormValues: Dispatch<SetStateAction<T>>;
	otp: {
		isOtp?: boolean;
		onSubmit?: (
			otp: string,
			setError: React.Dispatch<React.SetStateAction<boolean>>,
		) => Promise<void>;
		email: string;
	};
	submit?: {
		isSubmitActive?: boolean;
		isSubmitApproved?: boolean;
		onSubmit?: (values: T, formikHelpers: FormikHelpers<any>) => Promise<any>;
	};
	goToNextStep?: () => void;
	setIsFormValid: React.Dispatch<React.SetStateAction<boolean>>;
	validationSchema: yup.ObjectSchema<
		yup.AnyObject,
		yup.AnyObject,
		yup.AnyObject,
		""
	>;
}
export class FormObject<T> {
	options: FormOptions<T> | null;
	constructor(options: FormOptions<T> | null) {
		if (options) this.options = options;
		else this.options = null;
	}
	getFields(): FormField[] {
		return Object.getOwnPropertyNames(this).reduce((acc: any, curr: string) => {
			if (["options"].includes(curr)) return acc;
			acc.push((this as any)[curr]);
			return acc;
		}, []) as FormField[];
	}
	getInitialValues(): T {
		return Object.getOwnPropertyNames(this).reduce((acc: any, curr: string) => {
			if (["options"].includes(curr)) return acc;
			acc[(this as any)[curr]["name"]] = "";
			return acc;
		}, {}) as T;
	}
	getProps(): FormProps<T> {
		if (!this.options) throw new Error("No options passed");
		return {
			formName: this.options.formName,
			fields: this.getFields(),
			initialValues: this.getInitialValues(),
			options: {
				otp: {
					isOtp: this.options.otp.isOtp || false,
					onSubmit: this.options.otp.onSubmit,
					email: this.options.otp.email,
				},
				submit: {
					isSubmitActive: this.options.submit?.isSubmitActive || false,
					isSubmitApproved: this.options.submit?.isSubmitApproved || false,
					onSubmit: this.options.submit?.isSubmitActive
						? this.options?.submit!.onSubmit
						: async (_: T, __: FormikHelpers<any>) => {
								//
							},
				},
			},
			goToNextStep: this.options.goToNextStep,
			setIsFormValid: this.options.setIsFormValid,
			validationSchema: this.options.validationSchema,
			setFormValues: this.options.setFormValues,
		};
	}
}
export interface FormField {
	name: string;
	displayName: string;
	type: "text" | "password" | "phone" | "select" | "country";
	text?: { fullWidth?: boolean; multiLine?: boolean; rows?: number };
	select?: { title: string; values: string[]; multiple?: boolean };
	size?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
	margin?: number;
}

export interface FormFieldGroup {
	direction: "row" | "column";
	values: FormField[];
	name: string;
}
export interface FormGroup {
	groups: FormFieldGroup[];
	container: "row" | "column";
}
export interface FormProps<T> {
	formName: string;
	fields?: FormField[];
	group?: FormGroup;
	initialValues: T;
	options: {
		otp: {
			isOtp: boolean;
			onSubmit?: (
				otp: string,
				setError: React.Dispatch<React.SetStateAction<boolean>>,
			) => Promise<void>;
			email: string;
		};
		submit: {
			onSubmit?: (values: T, formikHelpers: FormikHelpers<any>) => Promise<any>;
			isSubmitApproved: boolean;
			isSubmitActive: boolean;
		};
	};
	setFormValues: React.Dispatch<React.SetStateAction<T>>;
	setIsFormValid: React.Dispatch<React.SetStateAction<boolean>>;
	validationSchema: yup.ObjectSchema<
		yup.AnyObject,
		yup.AnyObject,
		yup.AnyObject,
		""
	>;
	goToNextStep?: () => void;
	fieldValueLoaded?: keyof RegisterNewSetp1LoginInformationValues | null;
}
const Form = (props: FormProps<any>) => {
	if (typeof props.group === "undefined" && typeof props.fields === "undefined")
		throw new Error("Groups or fields must be defined");
	const { t } = useTranslation();
	const theme = useTheme();
	const breakPoint = useBreakpoint();
	const formik = useFormik({
		initialValues: props.initialValues,
		validateOnMount: true,
		validateOnChange: false,
		validationSchema: props.validationSchema,
		onSubmit: (values: any, formikHelpers: FormikHelpers<any>) => {
			if (props?.options?.submit?.onSubmit)
				props?.options!.submit!.onSubmit!(values, formikHelpers);
			const fields =
				props.fields?.filter((f) => f.type === "password") ||
				props.group?.groups
					.filter(
						(g) => g.values.filter((f) => f.type === "password").length > 0,
					)
					.map((g) => g.values.filter((g) => g.type === "password"))
					.flatMap((v) => v);
			fields?.forEach((f) => formik.setFieldValue(f.name, ""));
		},
		validateOnBlur: true,
	});
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [openOtp, setOpenOtp] = useState(false);
	const debouncedValidate = useMemo(
		() => debounce(formik.validateForm, 500),
		[formik.validateForm],
	);
	useEffect(() => {
		debouncedValidate(formik.values);
	}, [formik.values, debouncedValidate]);
	let prev: any = null;
	const countryNode = (field: FormField): JSX.Element => {
		return (
			<Grid item xs={12} md={6}>
				<FormControl
					fullWidth
					onBlur={(e) => {
						formik!.handleBlur(e);
						formik.validateForm();
					}}
				>
					<CountryPickerDb
						onChange={(str) => formik.setFieldValue(field.name, str)}
						multiple={false}
						defaultByIP={true}
					/>
				</FormControl>
			</Grid>
		);
	};

	const selectNode = (field: FormField): JSX.Element => {
		const selectedValue = field.select?.title;
		return (
			<Grid
				key={`${field.name}-container`}
				item
				container
				xs={12}
				md={field.size || 6}
				maxWidth={"100%"}
			>
				<Select
					key={`${field.name}-select`}
					// defaultValue={field?.select?.multiple ? [selectedValue] : selectedValue}
					inputProps={{ disableunderline: "true" }}
					fullWidth
					name={field.name}
					multiple={field?.select?.multiple || false}
					value={
						field?.select?.multiple
							? [formik.values[field.name] || selectedValue]
							: formik.values[field.name] || selectedValue
					}
					sx={{
						color: theme.palette.text.primary,
						marginBottom: field?.margin || 0,
					}}
					label={t(field.select!.title)}
					renderValue={(selected) => {
						if (field?.select?.multiple && Array.isArray(selected[0]))
							return selected[0].map((v: string) => t(v)).join(", ");
						if (selected === selectedValue) {
							return (
								<Typography color="text.hint">
									{t(field.select!.title)}
								</Typography>
							);
						}
						return selected;
					}}
					onChange={(event: SelectChangeEvent<any>) => {
						if (field?.select?.multiple || false) {
							if (
								formik.values[field.name].includes(
									event.target.value[event.target.value.length - 1] as string,
								)
							)
								formik.setFieldValue(field.name, [
									...formik.values[field.name].slice(
										0,
										formik.values[field.name].indexOf(
											event.target.value[
												event.target.value.length - 1
											] as string,
										),
									),
									...formik.values[field.name].slice(
										formik.values[field.name].indexOf(
											event.target.value[
												event.target.value.length - 1
											] as string,
										) + 1,
									),
								]);
							else
								formik.setFieldValue(field.name, [
									...formik.values[field.name],
									event.target.value[event.target.value.length - 1] as string,
								]);
						} else formik.setFieldValue(field.name, event.target.value);
					}}
					onBlur={(e) => {
						formik!.handleBlur(e);
						formik.validateForm();
					}}
					error={
						formik!.touched![field.name] && Boolean(formik!.errors![field.name])
					}
				>
					<MenuItem
						key={`${field.name}-default-menu-item`}
						disabled
						value={selectedValue}
					>
						{t(field.select!.title)}
					</MenuItem>
					{field.select!.values.map((val: string) => {
						if (field?.select?.multiple)
							return (
								<MenuItem
									key={`${props.formName}-${field.name}-${val}`}
									value={val}
								>
									<Checkbox
										checked={formik.values[field.name].indexOf(val) > -1}
									/>
									<ListItemText primary={val} />
								</MenuItem>
							);
						else
							return (
								<MenuItem
									key={`${props.formName}-${field.name}-${val}`}
									value={val.toLowerCase()}
								>{`${val.charAt(0).toLocaleUpperCase()}${val.slice(
									1,
								)}`}</MenuItem>
							);
					})}
				</Select>
			</Grid>
		);
	};
	const phoneNode = (field: FormField): JSX.Element => {
		return (
			<Grid item xs={12} md={6}>
				<Phone
					countryCode={formik.values["country"]}
					onRes={(e: any) => {
						if (e.isValid) {
							formik.setFieldValue(field.name, e.phone);
							formik.validateForm();
						} else {
							formik.setFieldValue(field.name, "");
						}
					}}
				/>
			</Grid>
		);
	};
	const textNode = (field: FormField): JSX.Element => {
		return (
			<Grid
				item
				xs={12}
				md={6}
				key={`${field.name}-container`}
				sx={{ position: "relative" }}
			>
				<TextField
					InputProps={{ disableUnderline: true }}
					autoComplete="off"
					placeholder={t(field.displayName)!}
					variant="standard"
					name={field.name}
					fullWidth={field.text?.fullWidth || true}
					multiline={field.text?.multiLine || false}
					value={formik!.values![field.name]}
					onChange={formik!.handleChange!}
					onBlur={(e) => {
						formik!.handleBlur(e);
						formik.validateForm();
						formik.setFieldValue(
							field.name,
							formik!.values![field.name].trim(),
						);
					}}
					sx={{ marginLeft: { md: field?.margin || 0 } }}
					error={
						formik!.touched![field.name] && Boolean(formik!.errors![field.name])
					}
					helperText={
						<>{formik!.touched![field.name] && formik!.errors![field.name]}</>
					}
					key={`${field.name}-input`}
					rows={field?.text?.rows || 1}
				/>
				{props.fieldValueLoaded === field.name && (
					<Box sx={{ position: "absolute", top: 30, right: 10 }}>
						<CircularProgress size={20} />
					</Box>
				)}
			</Grid>
		);
	};
	const passwordNode = (field: FormField): JSX.Element => {
		return (
			<Grid item xs={12} md={6}>
				<TextField
					name={field.name}
					autoComplete="off"
					onChange={formik.handleChange}
					onBlur={(e) => {
						formik!.handleBlur(e);
						formik.validateForm();
					}}
					value={formik!.values![field.name]}
					error={
						formik!.touched![field.name] && Boolean(formik!.errors![field.name])
					}
					helperText={
						<>{formik!.touched![field.name] && formik!.errors![field.name]}</>
					}
					fullWidth
					placeholder={t("Password")!}
					type={showPassword ? "text" : "password"}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? <Visibility /> : <VisibilityOff />}
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Grid>
		);
	};
	const isListContainsPasswordNode: boolean = !props.group
		? props!.fields!.filter((item) => item.type === "password").length > 0
		: props.group.groups.filter(
				(group) =>
					group.values.filter((field) => field.type === "password").length > 0,
			).length > 0;
	const isPassContainsOneLetter = checkIfStringHasAtLeastOneNumber(
		formik.values.password,
	);
	const isPassContainsUpperCase = checkIfStringHasUpperCase(
		formik.values.password,
	);
	const isPassContainsLowerCase = checkIfStringHasLowerCase(
		formik.values.password,
	);
	const isPassContains15Chars = checkIfStringHas15Characters(
		formik.values.password,
	);
	const isPassValid =
		!isListContainsPasswordNode ||
		(isPassContainsOneLetter &&
			isPassContainsUpperCase &&
			isPassContainsLowerCase &&
			isPassContains15Chars);

	useEffect(() => {
		props.setIsFormValid(formik.isValid && formik.dirty && isPassValid);
		if (formik.isValid && formik.dirty && isPassValid)
			props.setFormValues(formik.values);
	}, [formik.isValid, formik.dirty, isPassValid]);

	const mapFieldToNode = (field: FormField) => {
		if (field.type === "text") return textNode(field);
		if (field.type === "password") return passwordNode(field);
		if (field.type === "select") return selectNode(field);
		if (field.type === "phone") return phoneNode(field);
		if (field.type === "country") return countryNode(field);
		else return <></>;
	};

	const nodes: JSX.Element[] = !props.group
		? props!
				.fields!.map(mapFieldToNode)
				.reduce((acc: JSX.Element[], curr: any, idx: number) => {
					const isLast = idx === props!.fields!.length;
					if (idx % 2 === 0) prev = curr;
					if (idx % 2 === 1 || isLast)
						acc.push(
							<Grid
								item
								container
								xs={12}
								spacing={2}
								mb={2}
								key={`${props.formName}-form-group-${idx}`}
							>
								{!isLast && prev}
								{curr}
							</Grid>,
						);
					return acc;
				}, [])
		: props!.group!.groups.map((group) => {
				const groupJSX = group.values.map(mapFieldToNode);
				if (group.direction === "row")
					return (
						<Grid
							item
							container
							key={`group-container-${group.name}`}
							direction="row"
							xs={12}
							md={6}
						>
							{groupJSX}
						</Grid>
					);
				if (group.direction === "column")
					return (
						<Grid
							key={`group-container-${group.name}`}
							item
							container
							direction="column"
							xs={12}
							md={6}
						>
							{groupJSX}
						</Grid>
					);
				else return <></>;
			});
	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
			}}
		>
			{props?.group ? (
				<Grid
					key={"group-container-all"}
					container
					item
					sx={{ flexDirection: { xs: "column", md: "row" } }}
					xs={12}
				>
					{nodes}
				</Grid>
			) : (
				nodes
			)}

			{Boolean(isListContainsPasswordNode) && (
				<Grid mb={5} container>
					<Grid item xs={12} lg={6}>
						<Bullet
							checked={isPassContainsOneLetter}
							label={t("At least one number")}
						/>
						<Bullet
							checked={isPassContainsUpperCase}
							label={t("One upper case letter")}
						/>
					</Grid>
					<Grid item xs={12} lg={6}>
						<Bullet
							checked={isPassContainsLowerCase}
							label={t("One lower case letter")}
						/>
						<Bullet
							checked={isPassContains15Chars}
							label={t("At least 15 characters")}
						/>
					</Grid>
				</Grid>
			)}
			{props.options.submit.isSubmitActive && (
				<Grid
					alignContent="flex-end"
					alignItems="flex-end"
					justifyContent="space-between"
					container
					flexBasis={{ xs: "50px", md: "240px" }}
					marginTop={1}
					item
					xs={12}
					color="text.primary"
				>
					<Grid item xs={12} md={6} marginTop={2}>
						<Typography color="text.primary">
							{t("Already have an account?")}{" "}
							<Link
								to="/login"
								style={{
									color: theme.palette.text.secondary,
									textDecoration: "none",
								}}
							>
								{t("Sign in")}
							</Link>
						</Typography>
					</Grid>
					<Grid item container justifyContent="flex-end" xs={12} md={6}>
						<Button
							color="secondary"
							id="buttonSubmit"
							onClick={
								props?.options.otp.isOtp
									? async () => {
											// e.preventDefault();
											await formik.submitForm();
											setOpenOtp(true);
										}
									: async () => {
											// e.preventDefault();
											await formik.submitForm();
											props.goToNextStep!();
										}
							}
							sx={{ px: 5 }}
							variant="contained"
							type="submit"
							fullWidth={breakPoint.getBreakPointName() === "xs" ? true : false}
							disabled={!(props.options.submit.isSubmitApproved || false)}
						>
							{t("Next")}
						</Button>
					</Grid>
					{props?.options.otp.isOtp && (
						<ConfirmOtp
							onResend={props.options.submit.onSubmit as () => Promise<void>}
							open={openOtp}
							setOpen={setOpenOtp}
							handleNext={props.goToNextStep!}
							onSubmit={props.options.otp.onSubmit!}
							email={props.options.otp.email}
						/>
					)}
				</Grid>
			)}
		</form>
	);
};
export default Form;
